/**
 * D849 (P0-013) - the store's opening hours as the Admin edits them: a 7-day grid, each day closed, open 24 hours or open between two
 * times. The store profile keeps them as half-open minute-of-week windows (`PUT /v1/store-profile/profiles/:locationId/hours`), minute 0
 * being Monday 00:00 in the store's market zone (the same count the desk's open/closed gate reads). This module converts both ways and
 * names a problem before the round trip; the server stays the authority (`validateStoreHours`).
 *
 * A closing time at or before the opening time means the store closes after midnight (22:00 to 02:00): the window runs into the next
 * day, and Sunday's wraps into Monday. A week with no open day is not a schedule - a store with no hours is never "open".
 */
export const HOURS_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type HoursDay = (typeof HOURS_DAYS)[number];

export type DayHours =
  | { readonly mode: 'closed' }
  | { readonly mode: 'all_day' }
  | { readonly mode: 'window'; readonly open: string; readonly close: string };
export type WeekHours = Readonly<Record<HoursDay, DayHours>>;

export interface WeeklyWindow {
  readonly startMinuteOfWeek: number;
  readonly endMinuteOfWeek: number;
}

export type StoreHoursIssue = 'NO_OPEN_DAY' | `TIME_INVALID:${HoursDay}` | `TIME_EQUAL:${HoursDay}`;

const DAY = 1440;
const WEEK = 10080;
const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;
const minuteOf = (hhmm: string): number | undefined => {
  const m = TIME.exec(hhmm);
  return m === null ? undefined : Number(m[1]) * 60 + Number(m[2]);
};
const hhmm = (minute: number): string => `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;

/** Every day closed - where a store with no schedule starts. */
export function emptyWeek(): WeekHours {
  return { mon: { mode: 'closed' }, tue: { mode: 'closed' }, wed: { mode: 'closed' }, thu: { mode: 'closed' }, fri: { mode: 'closed' }, sat: { mode: 'closed' }, sun: { mode: 'closed' } };
}

/** The same hours on every day (the grid's quick start: 10:00 to 22:00 is typed once, not seven times). */
export function sameEveryDay(day: DayHours): WeekHours {
  return { mon: day, tue: day, wed: day, thu: day, fri: day, sat: day, sun: day };
}

export function setDay(week: WeekHours, day: HoursDay, next: DayHours): WeekHours {
  return { ...week, [day]: next };
}

/** The grid as weekly windows, or every problem at once. */
export function weekToWindows(week: WeekHours): { readonly ok: true; readonly windows: readonly WeeklyWindow[] } | { readonly ok: false; readonly issues: readonly StoreHoursIssue[] } {
  const windows: WeeklyWindow[] = [];
  const issues: StoreHoursIssue[] = [];
  HOURS_DAYS.forEach((day, index) => {
    const h = week[day];
    const base = index * DAY;
    if (h.mode === 'all_day') windows.push({ startMinuteOfWeek: base, endMinuteOfWeek: base + DAY });
    else if (h.mode === 'window') {
      const open = minuteOf(h.open);
      const close = minuteOf(h.close);
      if (open === undefined || close === undefined) issues.push(`TIME_INVALID:${day}`);
      else if (open === close) issues.push(`TIME_EQUAL:${day}`);
      else {
        const length = (close - open + DAY) % DAY;
        windows.push({ startMinuteOfWeek: base + open, endMinuteOfWeek: (base + open + length) % WEEK });
      }
    }
  });
  if (issues.length === 0 && windows.length === 0) issues.push('NO_OPEN_DAY');
  return issues.length > 0 ? { ok: false, issues } : { ok: true, windows };
}

/**
 * The grid for a schedule the server already holds, when it fits the grid (at most one window starting on each day, none longer than a
 * day). `undefined` when it does not - the editor then starts empty rather than show hours it cannot represent.
 */
export function weekFromWindows(windows: readonly WeeklyWindow[]): WeekHours | undefined {
  let week = emptyWeek();
  const seen = new Set<HoursDay>();
  for (const w of windows) {
    const index = Math.floor(w.startMinuteOfWeek / DAY);
    const day = HOURS_DAYS[index];
    if (day === undefined || seen.has(day)) return undefined;
    seen.add(day);
    const length = (((w.endMinuteOfWeek - w.startMinuteOfWeek) % WEEK) + WEEK) % WEEK || WEEK;
    if (length > DAY) return undefined;
    const open = w.startMinuteOfWeek - index * DAY;
    week = setDay(week, day, length === DAY && open === 0 ? { mode: 'all_day' } : { mode: 'window', open: hhmm(open), close: hhmm((open + length) % DAY) });
  }
  return week;
}

/** True when the schedule has at least one open window - the only kind of hours that lets a store show as open. */
export function hasOpenHours(hours: { readonly regular: readonly unknown[] } | undefined): boolean {
  return hours !== undefined && hours.regular.length > 0;
}
